import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router'
import DynamicSurveyField from 'src/components/survey/dynamic-survey-field'
import { Button } from 'src/components/button'
import { PinSetupForm } from 'src/components/survey/pin-setup-form'
import { Spinner } from 'src/components/spinner'
import { useSurvey } from 'src/hooks/survey/use-survey'
import { SurveyProvider, useSurveyForm } from 'src/hooks/survey/survey-provider'
import { LANGUAGE_STORAGE_KEY } from 'src/i18n/config'
import { useSurveyPin } from 'src/stores/survey-pin'
import { getGuestSurveyLink } from 'src/services/guest-survey'
import { reservationGuestApi } from 'src/services/reservation-guests'
import { resolveReservationRef, useGuestSurveyFinish } from 'src/hooks/use-guest-survey-finish'
import usaFlag from 'src/assets/images/usa.svg'
import colombiaFlag from 'src/assets/images/colombia.svg'

const LanguagePicker = ({ onSelect }: { onSelect: (lang: string) => void }) => {
  const { t } = useTranslation()
  return (
    <div className="bg-bastaNavy flex h-[calc(100vh-350px)] w-full flex-col">
      <div className="mx-auto flex h-full w-full flex-col items-center justify-center rounded-lg border border-border bg-surface p-4 shadow-sm sm:w-[600px] md:w-[800px]">
        <div className="flex w-full max-w-sm flex-col gap-4">
          <Button
            variant="unstyled"
            onClick={() => onSelect('en')}
            className="flex items-center gap-4 rounded-lg border-2 border-border bg-surface px-6 py-4 text-lg font-medium text-text-primary transition-colors hover:border-accent hover:bg-surface-hover"
          >
            <img src={usaFlag} alt={t('language.usFlagAlt')} className="h-6 w-8" />
            {t('language.englishName')}
          </Button>
          <Button
            variant="unstyled"
            onClick={() => onSelect('es')}
            className="flex items-center gap-4 rounded-lg border-2 border-border bg-surface px-6 py-4 text-lg font-medium text-text-primary transition-colors hover:border-accent hover:bg-surface-hover"
          >
            <img src={colombiaFlag} alt={t('language.colombiaFlagAlt')} className="h-6 w-8" />
            {t('language.spanishName')}
          </Button>
        </div>
      </div>
    </div>
  )
}

export { resolveReservationRef }

/**
 * The guest link token and Contact email PIN setup needs. A guest link passes its
 * own token; the staff flow mints one for the reservation and prefills the email
 * of the guest being escalated or updated.
 */
const usePinSetupContext = ({
  enabled,
  guestToken,
  reservationId,
  reservationGuestId,
}: {
  enabled: boolean
  guestToken?: string
  reservationId?: string
  reservationGuestId?: string
}) => {
  const { data: link } = useQuery({
    queryKey: ['guest-survey-link', reservationId],
    queryFn: () => getGuestSurveyLink(reservationId ?? ''),
    enabled: enabled && !guestToken && Boolean(reservationId),
  })
  const { data: reservationGuest } = useQuery({
    queryKey: ['reservation-guest', reservationGuestId],
    queryFn: () => reservationGuestApi.retrieve(reservationGuestId ?? ''),
    enabled: enabled && !guestToken && Boolean(reservationGuestId),
  })
  return { token: guestToken ?? link?.token, defaultEmail: reservationGuest?.guestRef.email }
}

/**
 * `instanceId` opens that existing instance instead of the route's `formId` or a new one.
 * `guestToken` is set on the guest link; without it the survey is the staff flow.
 */
export const SurveyForm = ({
  onFinish,
  instanceId,
  guestToken,
}: {
  onFinish: () => void
  instanceId?: string
  guestToken?: string
}) => {
  const { formId: routeFormId, id: pathReservationId, guestId } = useParams()
  const formId = instanceId ?? routeFormId
  const [searchParams] = useSearchParams()
  const reservationId = resolveReservationRef(searchParams.get('reservation'), pathReservationId)
  const { t, i18n } = useTranslation()
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(formId ? 'en' : null)

  const {
    saveAndNext,
    questionChoiceOptions,
    createSurveyInstance,
    currentQuestion,
    previousQuestion,
    loading,
    loadingOptions,
    isLast,
    loadingExistingInstance,
    errors,
    handleSearch,
    setIsFinished,
  } = useSurvey(onFinish, selectedLanguage ?? 'en')
  const {
    responseRef,
    response,
    setResponse,
    componentType,
    instance,
    setInstanceId,
    isUploading,
  } = useSurveyForm()

  // A secure instance cannot be answered until the respondent has chosen a PIN.
  const pinChosenFor = useSurveyPin((s) => s.instanceId)
  const needsPinSetup = !!instance?.requiresPinSetup && pinChosenFor !== instance.id
  const pinSetup = usePinSetupContext({
    enabled: needsPinSetup,
    guestToken,
    reservationId,
    reservationGuestId: searchParams.get('escalate') ?? guestId,
  })

  const handleLanguageSelect = (lang: string) => {
    i18n.changeLanguage(lang)
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang)
    setSelectedLanguage(lang)
  }

  useEffect(() => {
    if (formId) {
      setInstanceId(formId.toString())
    } else if (selectedLanguage) {
      createSurveyInstance({ reservationId })
    }
  }, [createSurveyInstance, formId, setInstanceId, reservationId, selectedLanguage])

  const nextAction = useMemo(() => {
    return isLast
      ? (newResponse: any = response) => {
          setIsFinished(true)
          saveAndNext({
            instance: instance?.id,
            question: currentQuestion?.id,
            value: newResponse,
          })
        }
      : (newResponse: any) => {
          saveAndNext({
            instance: instance?.id,
            question: currentQuestion?.id,
            value: newResponse,
          })
        }
  }, [isLast, response, saveAndNext, instance?.id, currentQuestion?.id, setIsFinished])

  const handleSurveyResponse = (value: string | null | undefined) => {
    setResponse(value)
  }

  if (!formId && !selectedLanguage) {
    return <LanguagePicker onSelect={handleLanguageSelect} />
  }

  return (
    <div className="bg-bastaNavy flex h-[calc(100vh-350px)] w-full flex-col">
      {loadingExistingInstance ? (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <Spinner size="lg" />
        </div>
      ) : needsPinSetup && instance ? (
        <div className="relative mx-auto flex h-full w-full flex-col rounded-lg border border-border bg-surface p-4 shadow-sm sm:w-[600px] md:w-[800px]">
          {pinSetup.token ? (
            <PinSetupForm
              instanceId={instance.id}
              token={pinSetup.token}
              defaultEmail={pinSetup.defaultEmail}
            />
          ) : (
            <div className="flex flex-1 items-center justify-center">
              <Spinner size="lg" />
            </div>
          )}
        </div>
      ) : currentQuestion ? (
        <div className="relative mx-auto flex h-full w-full flex-col rounded-lg border border-border bg-surface p-4 shadow-sm sm:w-[600px] md:w-[800px]">
          {/* Question Header - Fixed at top */}

          <div className="mb-4 flex-shrink-0">
            <p dangerouslySetInnerHTML={{ __html: currentQuestion?.question ?? '' }}></p>
          </div>
          {/* Content Area - Scrollable */}
          <div className="mb-4 flex-grow overflow-y-auto">
            {currentQuestion && componentType(currentQuestion) && (
              <div className="pb-3">
                <DynamicSurveyField
                  variant={'primary'}
                  component={componentType(currentQuestion)}
                  handleResponse={handleSurveyResponse}
                  handleSearch={handleSearch}
                  value={response}
                  options={questionChoiceOptions?.results ?? []}
                  loading={loadingOptions}
                  valueRef={responseRef}
                  disabled={loading}
                  isMultiple={currentQuestion?.questionType === 'MULTIPLE_OPTION'}
                  isSearchable={currentQuestion?.questionDisplay === 'DROPDOWN'}
                  questionId={currentQuestion?.id}
                  questionDisplay={currentQuestion?.questionDisplay}
                  layout={currentQuestion?.layout}
                  validatorsRef={currentQuestion?.validatorsRef as any}
                />
                {errors.length > 0 && <strong className="text-error">{errors.join(',')}</strong>}
              </div>
            )}
          </div>
          {/* Navigation Buttons - Fixed at bottom */}
          <div className="mt-auto flex flex-shrink-0 justify-between space-x-5">
            {instance?.templateRef?.firstQuestion !== currentQuestion?.id ? (
              <Button
                disabled={loading || isUploading}
                variant="secondary"
                onClick={() => previousQuestion(instance?.id ?? '')}
                isLoading={loading}
              >
                {t('common.back')}
              </Button>
            ) : (
              <div></div>
            )}
            <Button
              disabled={loading || isUploading}
              variant="primary"
              onClick={() => nextAction(response)}
              isLoading={loading}
              className="ml-auto"
            >
              {instance?.templateRef?.lastQuestion === currentQuestion?.id
                ? t('common.finish')
                : t('common.next')}
            </Button>
          </div>
          {/* Loading Overlay */}
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
              <Spinner size="lg" />
            </div>
          )}
        </div>
      ) : null}
    </div>
  )
}

export const ReservationGuestFormWrapper = () => {
  const { onFinish, isPending } = useGuestSurveyFinish()
  return (
    <SurveyProvider>
      {isPending ? (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <Spinner size="lg" />
        </div>
      ) : (
        <SurveyForm onFinish={onFinish} />
      )}
    </SurveyProvider>
  )
}
